import * as Sentry from '@sentry/react'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import 'leaflet/dist/leaflet.css'
import './index.css'
import App from './App.tsx'
import { isBrowserNoise } from './lib/observability/browserNoise'
import { purgeLegacyStorage } from './lib/utils/legacyStorage'

// Drop storage left by retired features (the old /admin audit buffer).
purgeLegacyStorage()

/**
 * Impersonation links carry `authToken=` in the query string. Sentry records
 * full URLs in two independent channels —
 * event breadcrumbs (`beforeBreadcrumb`) and Session Replay network frames
 * (`beforeAddRecordingEvent`) — so both must scrub before anything leaves the
 * browser. Error replays sample at 100%, so a defect on a landing with that
 * param would otherwise ship it in the replay network timeline.
 */
const SENSITIVE_QUERY_PARAMS = ['authToken']

const scrubUrl = (url: string) => {
  try {
    const parsed = new URL(url, window.location.origin)
    let changed = false
    for (const param of SENSITIVE_QUERY_PARAMS) {
      if (parsed.searchParams.has(param)) {
        parsed.searchParams.set(param, '[redacted]')
        changed = true
      }
    }
    return changed ? parsed.toString() : url
  } catch {
    return url
  }
}

Sentry.init({
  dsn: 'https://599a8225aef6bc4afb31b46a0b811784@o4508542910726144.ingest.de.sentry.io/4508542919114832',
  enabled: window.location.hostname !== 'localhost',
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  enableLogs: true,
  integrations: [
    Sentry.replayIntegration({
      beforeAddRecordingEvent(event) {
        const { tag, payload } = event.data
        if (tag === 'performanceSpan' && typeof payload.description === 'string') {
          payload.description = scrubUrl(payload.description)
        } else if (tag === 'breadcrumb' && payload.data) {
          const data = payload.data as Record<string, unknown>
          for (const key of ['url', 'from', 'to']) {
            if (typeof data[key] === 'string') data[key] = scrubUrl(data[key] as string)
          }
        }
        return event
      },
    }),
  ],
  beforeBreadcrumb(breadcrumb) {
    for (const key of ['url', 'from', 'to'] as const) {
      const value = breadcrumb.data?.[key]
      if (typeof value === 'string') breadcrumb.data![key] = scrubUrl(value)
    }
    return breadcrumb
  },
  beforeSend(event) {
    if (isBrowserNoise(event)) return null
    return event
  },
})

/**
 * Without this, an uncaught render error anywhere in the app is a white screen
 * with no Sentry Issue — the boundary is the only safety net for the paths the
 * per-feature observability helpers don't cover.
 */
const AppCrashFallback = () => (
  <div className="flex min-h-svh items-center justify-center bg-background p-6">
    <div className="max-w-sm space-y-3 text-center">
      <p className="text-sm font-medium text-foreground">Something went wrong</p>
      <p className="text-sm text-muted-foreground">
        The page failed to load. Our team has been notified.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded border border-border/40 px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted/60"
      >
        Reload page
      </button>
    </div>
  </div>
)

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<AppCrashFallback />}>
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
